import { useCallback, useEffect, useRef, useState } from 'react';

import { getTagItems } from '../services/tags';
import { normalizeId } from '../utils/ids';
import { getSessionUserId, useOnSessionChange } from '../utils/session';
import { rememberItemTagTitle, rememberItemTagTitles } from '../utils/tagTitleMemory';

/**
 * THE TAG PAGE's data (tags T5; keepr-api `GET /api/tags/{id}/items`, T3):
 * the tag, its rows a page at a time, the card and collection counts that
 * make its filters, and the first page's child chips.
 *
 *   useTagPage(tagId, { cardId, collectionId }) → {
 *     tag, total, items, cards, collections, children,
 *     loading       the first page of this tag + filters has not answered
 *     loadingMore   a later page is on its way
 *     notFound      the tag is missing, deleted, or not one this person may
 *                   open — ONE state for all of them, as the API answers one
 *                   404 for all of them (a malformed id's 400 reads the same)
 *     error         any other failure (the page offers to try again)
 *     hasMore, loadMore(), reload(), replaceItem(item)
 *   }
 *
 * A change of filter keeps the tag and its child chips on screen while the
 * rows are re-read (the header never flickers); a change of tag starts over.
 * An answer for an earlier question, or for another account, is dropped, and
 * a sign-out or another account signing in clears everything at once
 * (utils/session.js). Every item-tag title the rows carry is remembered for
 * the session (utils/tagTitleMemory), so their chips read by name.
 */
const EMPTY = Object.freeze({
	tag: null,
	total: 0,
	items: [],
	cards: [],
	collections: [],
	children: null,
	loading: true,
	loadingMore: false,
	notFound: false,
	error: null,
});

const isNotFound = error => {
	const status = error?.response?.status;
	return status === 404 || status === 400;
};

export default function useTagPage(tagId, { cardId = null, collectionId = null } = {}) {
	const id = normalizeId(tagId) || `${tagId || ''}`;
	const [state, setState] = useState(EMPTY);
	const stateRef = useRef(state);
	stateRef.current = state;
	const requestRef = useRef(0);
	const [attempt, setAttempt] = useState(0);

	const session = useOnSessionChange(() => {
		requestRef.current += 1;
		setState(EMPTY);
	});

	useEffect(() => {
		const token = ++requestRef.current;
		const startedFor = getSessionUserId();
		// The same tag under new filters keeps its header and child chips.
		setState(prev => (normalizeId(prev.tag?._id) === id && !prev.notFound
			? { ...prev, items: [], total: 0, loading: true, loadingMore: false, error: null }
			: EMPTY));
		getTagItems(id, { cardId, collectionId })
			.then(data => {
				if (token !== requestRef.current || getSessionUserId() !== startedFor) return;
				rememberItemTagTitles(data.items);
				if (data.tag?.kind === 'item' && typeof data.tag.title === 'string') rememberItemTagTitle(data.tag._id, data.tag);
				setState({
					tag: data.tag,
					total: data.total,
					items: data.items,
					cards: data.cards,
					collections: data.collections,
					children: data.children,
					loading: false,
					loadingMore: false,
					notFound: false,
					error: null,
				});
			})
			.catch(error => {
				if (token !== requestRef.current || getSessionUserId() !== startedFor) return;
				setState(isNotFound(error)
					? { ...EMPTY, loading: false, notFound: true }
					: { ...stateRef.current, loading: false, loadingMore: false, error });
			});
	}, [id, cardId, collectionId, session, attempt]);

	const loadMore = useCallback(() => {
		const current = stateRef.current;
		if (current.loading || current.loadingMore || current.items.length >= current.total) return;
		const token = requestRef.current;
		const startedFor = getSessionUserId();
		setState(prev => ({ ...prev, loadingMore: true }));
		getTagItems(id, { cardId, collectionId, skip: current.items.length })
			.then(data => {
				if (token !== requestRef.current || getSessionUserId() !== startedFor) return;
				rememberItemTagTitles(data.items);
				setState(prev => {
					const seen = new Set(prev.items.map(item => normalizeId(item?._id)));
					const fresh = data.items.filter(item => !seen.has(normalizeId(item?._id)));
					return { ...prev, items: [...prev.items, ...fresh], total: data.total, loadingMore: false };
				});
			})
			.catch(error => {
				if (token !== requestRef.current || getSessionUserId() !== startedFor) return;
				setState(prev => ({ ...prev, loadingMore: false, error }));
			});
	}, [id, cardId, collectionId]);

	/** A row as it stands after ⋯ Tag… on it: in its place. */
	const replaceItem = useCallback((item) => {
		const itemId = normalizeId(item?._id);
		if (!itemId) return;
		setState(prev => ({ ...prev, items: prev.items.map(row => (normalizeId(row?._id) === itemId ? item : row)) }));
	}, []);

	const reload = useCallback(() => setAttempt(n => n + 1), []);

	return {
		...state,
		hasMore: !state.loading && state.items.length < state.total,
		loadMore,
		reload,
		replaceItem,
	};
}
